/**
 * Background Check Page — Dashboard & management
 *
 * Route: /hr/background-checks
 */

import { useState } from 'react';
import { Shield, Clock, CheckCircle, AlertTriangle, XCircle, Send, RefreshCw, Settings } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { post } from '@/lib/api';

interface BackgroundCheck {
  id: string;
  employee_id: string;
  employee_name: string;
  package_name: string;
  status: string;
  result?: string;
  initiated_at: string;
  completed_at?: string;
  invitation_url?: string;
  position_type?: string;
}

interface BGConfig {
  checkr_enabled: boolean;
  default_package_slug: string;
  position_packages: Record<string, string>;
  auto_initiate_on_hire: boolean;
  conditional_employment: boolean;
  recheck_interval_months?: number;
  reminder_days: number[];
}

export function BackgroundCheckPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'settings'>('dashboard');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: checksData, refetch } = useApi<{ checks: BackgroundCheck[] }>(
    `background-checks${statusFilter ? `?status=${statusFilter}` : ''}`,
    [statusFilter],
  );
  const { data: expiringData } = useApi<{ expiring: BackgroundCheck[] }>('background-checks/expiring?days=30');
  const { data: config, refetch: refetchConfig } = useApi<BGConfig>('background-checks/config');

  const submitCheck = async (checkId: string) => {
    await post(`background-checks/${checkId}/submit`, {});
    await refetch();
  };

  const retryCheck = async (checkId: string) => {
    await post(`background-checks/${checkId}/retry`, {});
    await refetch();
  };

  const checks = checksData?.checks ?? [];
  const pending = checks.filter(c => c.status === 'pending' || c.status === 'invitation_sent');
  const inProgress = checks.filter(c => c.status === 'in_progress');
  const completed = checks.filter(c => c.status === 'completed');
  const expiring = expiringData?.expiring ?? [];

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Background Checks
        </h1>
        <div className="flex gap-1 border rounded-md">
          <button onClick={() => setActiveTab('dashboard')}
            className={`px-3 py-1.5 text-sm ${activeTab === 'dashboard' ? 'bg-muted font-medium' : ''}`}>
            Dashboard
          </button>
          <button onClick={() => setActiveTab('settings')}
            className={`px-3 py-1.5 text-sm flex items-center gap-1 ${activeTab === 'settings' ? 'bg-muted font-medium' : ''}`}>
            <Settings className="w-3 h-3" /> Settings
          </button>
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <div className="p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">{pending.length}</div>
                <div className="text-sm text-muted-foreground">Pending</div>
              </div>
            </Card>
            <Card>
              <div className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{inProgress.length}</div>
                <div className="text-sm text-muted-foreground">In Progress</div>
              </div>
            </Card>
            <Card>
              <div className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{completed.length}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
            </Card>
            <Card>
              <div className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{expiring.length}</div>
                <div className="text-sm text-muted-foreground">Expiring Soon</div>
              </div>
            </Card>
          </div>

          {/* Filter */}
          <div className="flex gap-2">
            {['', 'pending', 'invitation_sent', 'in_progress', 'completed', 'failed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-sm rounded-full border ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                {s || 'All'}
              </button>
            ))}
          </div>

          {/* Checks List */}
          <div className="space-y-2">
            {checks.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">No background checks found.</div>
            )}
            {checks.map(check => (
              <Card key={check.id}>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {statusIcon(check.status)}
                    <div>
                      <div className="font-medium">{check.employee_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {check.package_name} — {check.position_type ?? 'General'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={check.result === 'clear' ? 'completed' : check.result === 'consider' ? 'pending' : check.status} />
                    <span className="text-xs text-muted-foreground">{new Date(check.initiated_at).toLocaleDateString()}</span>
                    {check.status === 'invitation_sent' && (
                      <button onClick={() => submitCheck(check.id)} className="p-1.5 hover:bg-muted rounded-md" title="Submit">
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {check.status === 'failed' && (
                      <button onClick={() => retryCheck(check.id)} className="p-1.5 hover:bg-muted rounded-md" title="Retry">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Expiring Checks */}
          {expiring.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Expiring Checks (30 days)
              </h2>
              {expiring.map(check => (
                <Card key={check.id}>
                  <div className="p-3 flex items-center justify-between text-sm">
                    <span className="font-medium">{check.employee_name}</span>
                    <span className="text-muted-foreground">Re-check due: {check.completed_at ? new Date(check.completed_at).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'settings' && config && (
        <BGSettingsPanel config={config} onSaved={refetchConfig} />
      )}
    </div>
  );
}

function BGSettingsPanel({ config, onSaved }: { config: BGConfig; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const [localConfig, setLocalConfig] = useState(config);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/background-checks/config`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(localConfig),
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Background Check Settings</h2>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={localConfig.checkr_enabled}
            onChange={e => setLocalConfig(prev => ({ ...prev, checkr_enabled: e.target.checked }))} />
          <span>Enable Checkr integration</span>
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={localConfig.auto_initiate_on_hire}
            onChange={e => setLocalConfig(prev => ({ ...prev, auto_initiate_on_hire: e.target.checked }))} />
          <span>Auto-initiate on new hire</span>
        </label>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={localConfig.conditional_employment}
            onChange={e => setLocalConfig(prev => ({ ...prev, conditional_employment: e.target.checked }))} />
          <span>Conditional employment (restrict access until cleared)</span>
        </label>

        <div>
          <label className="text-sm font-medium">Default Package</label>
          <input type="text" value={localConfig.default_package_slug}
            onChange={e => setLocalConfig(prev => ({ ...prev, default_package_slug: e.target.value }))}
            className="w-full mt-1 px-3 py-2 border rounded-md" />
        </div>

        <div>
          <label className="text-sm font-medium">Re-check Interval (months, 0 = disabled)</label>
          <input type="number" value={localConfig.recheck_interval_months ?? 0}
            onChange={e => setLocalConfig(prev => ({ ...prev, recheck_interval_months: parseInt(e.target.value) || undefined }))}
            className="w-full mt-1 px-3 py-2 border rounded-md" min={0} max={60} />
        </div>

        <button onClick={save} disabled={saving} className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </Card>
  );
}
